/* eslint-disable max-lines */

import { useArtwork, useArtworkForm, useImagesWarmFastCache } from "@design-lab/entities/artwork";
import { calculatedArtworkDimensions } from "@design-lab/entities/artwork/lib/calculateDimensions";
import type { Side } from "@design-lab/shared";
import { useArtboard } from "@design-lab/widgets/artboard/model/useArtboard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { api, type models } from "jiffy-api";

export function useCompleteRefineSession(side: Side) {
  const { getMachineBySide } = useArtwork();
  const { form } = useArtworkForm();
  const { warmCache } = useImagesWarmFastCache();
  const { composition } = useArtboard();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["manualRefine", side, "complete"],
    mutationFn: async (
      params: models.dtf.manualRefine.completeSession.PathParams & {
        image?: Blob;
        manual_edit: boolean;
        remove_background: boolean;
        start_new_edit: boolean;
      }
    ): Promise<models.dtf.manualRefine.completeSession.Response> => {
      const {
        dtf_artwork_id: dtfArtworkId,
        image,
        manual_edit: manualEdit,
        remove_background: removeBackground,
        start_new_edit: startNewEdit
      } = params as any;

      let raw: any;
      if (image) {
        const formData = new FormData();
        formData.append("image", image);
        formData.append("manual_edit", String(manualEdit));
        formData.append("remove_background", String(removeBackground));
        formData.append("start_new_edit", String(startNewEdit));
        raw = await api.dtf.completeRefineSession({ dtf_artwork_id: dtfArtworkId }, formData, {
          raw: true,
          useAxiosForRequest: true,
          headers: { "Content-Type": undefined }
        });
      } else {
        raw = await api.dtf.completeRefineSession({ dtf_artwork_id: dtfArtworkId }, {
          manual_edit: manualEdit,
          remove_background: removeBackground,
          start_new_edit: startNewEdit
        } as any);
      }
      const response = objNamesToCamelCase(raw) as any;

      // Update artwork image and warm the cache before resolving
      const machine = getMachineBySide(side);
      const currentArtwork = machine.state.context.artwork as any;
      if (currentArtwork && response) {
        const updatedArtwork = {
          ...currentArtwork,
          manualEditUrl: response.manualEditUrl || null,
          manualEditNoTrimUrl: response.manualEditNoTrimUrl || null
        } as any;

        let cachedImage: { image?: HTMLImageElement } | undefined;
        if (manualEdit && updatedArtwork.manualEditUrl) {
          const [img] = await warmCache([updatedArtwork.manualEditUrl]);
          cachedImage = img;
        }

        machine.send({
          type: "reset",
          targetState: "finished",
          context: {
            artwork: updatedArtwork,
            artworkId: machine.state.context.artworkId,
            visitedStates: machine.state.context.visitedStates,
            cacheWarmingStatus: machine.state.context.cacheWarmingStatus,
            feedbackSelectedReason: undefined
          }
        });

        form.setValue(`info.${side}.editImageManual`, manualEdit);

        // Recalculate and set form size for the refined side(s)
        try {
          const surface = composition?.surfaces?.[side];
          if (surface && cachedImage?.image) {
            const dims = calculatedArtworkDimensions(cachedImage.image, surface);
            if (side === "back") {
              form.setValue(`${side}.size`, {
                width: dims.maxWidthInches,
                height: dims.maxHeightInches
              });
            } else {
              const frontDims = dims;
              form.setValue(`front.size`, {
                width: frontDims.maxWidthInches,
                height: frontDims.maxHeightInches
              });
              const flcSurface = composition?.surfaces?.frontLeftChest;
              if (flcSurface) {
                const flcDims = calculatedArtworkDimensions(cachedImage.image, flcSurface);
                form.setValue(`frontLeftChest.size`, {
                  width: flcDims.maxWidthInches,
                  height: flcDims.maxHeightInches
                });
              }
            }
          }
        } catch {
          // ignore dimension update errors
        }
      }

      return response;
    },
    onSuccess: (response, variables) => {
      const dtfArtworkId = variables.dtf_artwork_id;
      const responseData = response;

      const updateArtwork = (artwork: models.dtf.getArtworkHistory.Artworks) =>
        artwork.id === dtfArtworkId
          ? {
              ...artwork,
              ordered: {
                ...artwork.ordered,
                manualEditUrl: responseData.manualEditUrl || null,
                manualEditNoTrimUrl: responseData.manualEditNoTrimUrl || null,
                editImageManual: variables.manual_edit
              }
            }
          : artwork;

      // Update infinite query caches - iterate through all pages
      queryClient.setQueriesData(
        { queryKey: ["dtfArtworks", "infinite"] },
        (oldData: { pages: models.dtf.getArtworkHistory.Response[] }) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page: models.dtf.getArtworkHistory.Response) => ({
              ...page,
              artworks: page.artworks?.map(updateArtwork)
            }))
          };
        }
      );

      // Also update regular query caches (non-infinite)
      queryClient.setQueriesData(
        {
          queryKey: ["dtfArtworks"],
          predicate: query => !query.queryKey.includes("infinite")
        },
        (oldData: models.dtf.getArtworkHistory.Response) => {
          if (!oldData?.artworks) return oldData;

          return {
            ...oldData,
            artworks: oldData.artworks.map(updateArtwork)
          };
        }
      );
    }
  });
}
